import { retrieveUserV3, retrieveWegoUserInformation } from '@alexis/helpers/authentications';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

import { datadogLogger } from '@helpers/datadogLogger';

import { changeUser } from '@redux/actions/userActions';
import { changeUserV3 } from '@redux/actions/userV3Actions';
import { getDomainBasedApiBaseUrlState, getUserState } from '@redux/selectors';

import { User } from '@wegoTypes/authentication';
import { LoggedInType } from '@wegoTypes/loggedInType';
import { UserV3 } from '@wegoTypes/userV3';

const useUserStateSync = ({
  refreshUserOnMount = false,
}: { refreshUserOnMount?: boolean } = {}) => {
  const dispatch = useDispatch();
  const user = useSelector(getUserState);
  const domainBasedApiBaseUrl: string = useSelector(getDomainBasedApiBaseUrlState);
  const isMountedOnce = useRef(false);

  const fetchAndStoreUserInfo = async (
    loggedInType: LoggedInType,
    expireBy: number,
  ): Promise<User> => {
    const wegoUserInformation = await retrieveWegoUserInformation(domainBasedApiBaseUrl);
    const user: User = {
      loggedInType,
      displayName: !!wegoUserInformation.name.trim()
        ? wegoUserInformation.name
        : wegoUserInformation.email,
      title: wegoUserInformation.title,
      firstName: wegoUserInformation.first_name,
      lastName: wegoUserInformation.last_name,
      nationality: wegoUserInformation.nationality,
      countryCode: wegoUserInformation.country_code,
      phoneCountryCode: wegoUserInformation.phone_country_code,
      phoneNumber: wegoUserInformation.phone_number,
      email: wegoUserInformation.email,
      csRestoreId: wegoUserInformation.cs_restore_id,
      photoUrl: wegoUserInformation.photo_url ?? undefined,
      userHash: wegoUserInformation.user_hash,
      idHash: wegoUserInformation.id_hash,
      sprinklrUserHash: wegoUserInformation.sprinklr_user_hash,
      accountPhoneNumber: wegoUserInformation.account_phone_number,
      accountPhoneCountryCode: wegoUserInformation.account_phone_country_code,
      expireBy,
    };
    dispatch(changeUser(user));
    return user;
  };

  const fetchAndStoreUserV3Info = async (): Promise<UserV3> => {
    const userV3 = await retrieveUserV3(domainBasedApiBaseUrl);
    dispatch(changeUserV3(userV3));
    return userV3;
  };

  const refreshUserData = async () => {
    try {
      if (!user) {
        return;
      }
      await fetchAndStoreUserInfo(user.loggedInType, user.expireBy);
      await fetchAndStoreUserV3Info();
    } catch (error) {
      datadogLogger(error as Error, {
        description: 'Refresh User Data Failed',
        errorSource: 'in useUserStateSync.tsx',
      });
    }
  };

  useEffect(() => {
    if (refreshUserOnMount && !isMountedOnce.current && user && user.expireBy > Date.now()) {
      refreshUserData();
    }
    isMountedOnce.current = true;
  }, [refreshUserOnMount, user]);

  return {
    refreshUserData,
    fetchAndStoreUserInfo,
    fetchAndStoreUserV3Info,
  };
};

export default useUserStateSync;
